// noinspection JSUnusedGlobalSymbols

export const DynamicListTagName = "dynamic-list"
export const ListUpdatedEvent = "list:updated"

export type ListUpdatedEventDetail = { items: HTMLElement[] }
export type DynamicListItem = {
    [key: string]: any
    events?: {
        [K in keyof HTMLElementEventMap]?: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any
    } & {
        [key: string]: (this: HTMLElement, ev: Event | CustomEvent) => any
    }
}

type PlaceholderData = {
    type: "attribute" | "text"
    placeholderKey: string
    dataItemKey: string
    attributeName?: string
}

/** @final */
export class HTMLDynamicListElement<T extends DynamicListItem> extends HTMLElement {
    private usePropertyData = false
    private internalDataList: (DynamicListItem & T)[] = []
    private internalListContainerType = "ul"
    private internalListContainerClass = ""
    private internalHideWhenEmpty = false
    private listContainerElement: HTMLElement | null = null
    private templateElement: HTMLElement | null = null
    private placeholderMap: Map<string, Array<PlaceholderData>> = new Map()

    static get observedAttributes(): string[] {
        return ["template-selector", "data-list", "list-container-type", "container-class", "hide-when-empty"]
    }

    get templateSelector(): string | null {
        return this.getAttribute("template-selector")
    }
    set templateSelector(newSelector: string | null) {
        this.setTemplateSelector(newSelector)
    }

    private setTemplateSelector(newSelector: string | null) {
        this.updateTemplate(newSelector)
        this.renderList()

        if (newSelector === null) {
            this.removeAttribute("template-selector")
        } else {
            this.setAttribute("template-selector", newSelector)
        }
    }

    get dataList(): T[] {
        return this.internalDataList
    }
    set dataList(newData: T[]) {
        this.setDataList(newData)
    }

    private setDataList(newData: T[]) {
        this.usePropertyData = true
        this.internalDataList = newData

        this.renderList()

        if (newData.length === 0) {
            this.removeAttribute("data-list")
        }
    }

    get listContainerType(): string {
        return this.internalListContainerType
    }
    set listContainerType(newType: string) {
        this.setListContainerType(newType)
    }

    private setListContainerType(newType: string) {
        this.internalListContainerType = newType
        this.updateListContainer()
        this.renderList()

        if (newType === "ul") {
            this.removeAttribute("list-container-type")
        } else {
            this.setAttribute("list-container-type", newType)
        }
    }

    get containerClass(): string {
        return this.listContainerElement ? this.listContainerElement.className : ""
    }
    set containerClass(newClass: string) {
        this.setContainerClass(newClass)
    }

    private setContainerClass(newClass: string) {
        this.internalListContainerClass = newClass
        this.updateListContainer()
        this.renderList()

        if (newClass === "") {
            this.removeAttribute("container-class")
        } else {
            this.setAttribute("container-class", newClass)
        }
    }

    get hideWhenEmpty(): boolean {
        return this.internalHideWhenEmpty
    }

    set hideWhenEmpty(newHideWhenEmpty: boolean) {
        this.setHideWhenEmpty(newHideWhenEmpty)
    }

    private setHideWhenEmpty(newHideWhenEmpty: boolean) {
        this.internalHideWhenEmpty = newHideWhenEmpty
        this.updateListVisibility()

        if (newHideWhenEmpty) {
            this.setAttribute("hide-when-empty", "")
        } else {
            this.removeAttribute("hide-when-empty")
        }
    }

    connectedCallback(): void {
        // If any of the properties are set before the element is connected to the DOM,
        //  we need to make sure the element is updated accordingly
        if (this.templateSelector !== undefined && this.templateSelector !== null) {
            this.setTemplateSelector(this.templateSelector)
        }

        if (this.listContainerType !== undefined && this.listContainerType !== "ul") {
            this.setListContainerType(this.listContainerType)
        }

        if (this.dataList !== undefined && this.dataList.length > 0) {
            this.setDataList(this.dataList)
        }

        if (this.containerClass !== undefined && this.containerClass !== "") {
            this.setContainerClass(this.containerClass)
        }

        this.setHideWhenEmpty(this.hasAttribute("hide-when-empty"))

        this.updateListContainer()
        this.updateTemplate()
        this.updateListVisibility()
    }

    attributeChangedCallback(attributeName: string, _oldValue: string | null, newValue: string | null): void {
        if (attributeName === "list-container-type") {
            this.internalListContainerType = newValue || "ul"
            this.updateListContainer()
        } else if (attributeName === "template-selector") {
            this.updateTemplate(newValue)
        } else if (attributeName === "data-list") {
            if (this.usePropertyData) {
                if (newValue !== null && newValue !== "") {
                    console.info(
                        "Data list was last set via property, ignoring attribute change.\n" +
                            "Clear the property to be able to use the attribute again.",
                    )
                }
                return
            }
            this.updateDataList(newValue)
        } else if (attributeName === "container-class" && newValue !== null) {
            this.internalListContainerClass = newValue

            if (this.listContainerElement) {
                this.listContainerElement.className = newValue
            }
        } else if (attributeName === "hide-when-empty") {
            this.internalHideWhenEmpty = this.hasAttribute("hide-when-empty")
            this.updateListVisibility()
        }
    }

    private updateListContainer(): void {
        if (this.listContainerElement !== null) {
            this.removeChild(this.listContainerElement)
        }

        this.listContainerElement = document.createElement(this.internalListContainerType)
        this.listContainerElement.className = this.internalListContainerClass

        const headerElement: HTMLElement | null = this.querySelector("header")
        const footerElement: HTMLElement | null = this.querySelector("footer")

        if (!this.listContainerElement) {
            return
        }

        if (headerElement) {
            this.prepend(headerElement)
        }

        this.appendChild(this.listContainerElement)

        if (footerElement) {
            this.appendChild(footerElement)
        }

        this.renderList()
    }

    private updateDataList(newData: string | null): void {
        if (newData === null) {
            this.internalDataList = [] as T[]
            return
        }

        try {
            this.internalDataList = JSON.parse(newData)
            this.renderList()
            this.updateListVisibility()
        } catch (error) {
            console.error("Invalid JSON in data-list attribute", error)
        }
    }

    private updateListVisibility(): void {
        if (this.internalDataList.length === 0 && this.internalHideWhenEmpty) {
            this.setAttribute("hidden", "")
        } else {
            this.removeAttribute("hidden")
        }
    }

    private updateTemplate(selector: string | null = null): void {
        const templateInLightDom = this.querySelector("template") as HTMLTemplateElement | null

        if (templateInLightDom !== null) {
            const contentRoot = templateInLightDom.content.firstElementChild as HTMLElement | null

            if (contentRoot !== null) {
                this.templateElement = contentRoot.cloneNode(true) as HTMLElement
            }
        } else if (selector !== null) {
            this.templateElement = document.querySelector(selector)?.cloneNode(true) as HTMLElement | null
        }

        this.processTemplate()
        this.renderList()
    }

    private processTemplate(): void {
        this.placeholderMap.clear()

        if (this.templateElement === null) {
            return
        }

        const evaluator = new XPathEvaluator()
        const xpath = `
          //*[@*[contains(., '{') and contains(., '}') and contains(substring-before(., '}'), '{')]] | //*[text()[contains(., '{') and contains(., '}') and contains(substring-before(., '}'), '{')]]
          | self::*[@*[contains(., '{') and contains(., '}') and contains(substring-before(., '}'), '{')]] | self::*[text()[contains(., '{') and contains(., '}') and contains(substring-before(., '}'), '{')]]
        `

        const result: XPathResult = evaluator.evaluate(
            xpath,
            this.templateElement,
            null,
            XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
        )

        for (let i = 0; i < result.snapshotLength; i++) {
            const element: HTMLElement | null = result.snapshotItem(i) as HTMLElement | null
            if (!element) {
                continue
            }

            const placeholders: Array<PlaceholderData> = []
            const uniqueId: string = "data-dlm-" + i
            const attributes: Array<Attr> = Array.from(element.attributes)

            for (const attribute of attributes) {
                const matches: IterableIterator<RegExpMatchArray> = attribute.value.matchAll(/\{(\w+)}/g)

                for (const match of matches) {
                    const placeholderKey = match[0]
                    const dataKey = match[1]

                    placeholders.push({
                        type: "attribute",
                        attributeName: attribute.name,
                        placeholderKey,
                        dataItemKey: dataKey,
                    })
                }
            }

            for (const child of element.childNodes) {
                if (child.nodeType === Node.TEXT_NODE) {
                    const matches: IterableIterator<RegExpMatchArray> = (child.textContent?.matchAll(/\{(\w+)}/g) ||
                        []) as IterableIterator<RegExpMatchArray>

                    for (const match of matches) {
                        const placeholderKey: string = match[0]
                        const dataKey: string = match[1]

                        placeholders.push({ type: "text", placeholderKey, dataItemKey: dataKey })
                    }
                }
            }

            if (placeholders.length > 0) {
                element.setAttribute(uniqueId, `${placeholders.length}`)
                this.placeholderMap.set(uniqueId, placeholders)
            }
        }
    }

    private renderList(): void {
        requestAnimationFrame((): void => {
            if (this.templateElement === null || this.listContainerElement === null) {
                return
            }

            this.listContainerElement.innerHTML = ""

            const newListItems: HTMLElement[] = []
            for (const currentDataItem of this.internalDataList) {
                const itemElement = this.templateElement.cloneNode(true) as HTMLElement

                for (const [uniqueId, placeholders] of this.placeholderMap) {
                    const element: HTMLElement | null = itemElement.hasAttribute(uniqueId)
                        ? itemElement
                        : (itemElement.querySelector(`[${uniqueId}]`) as HTMLElement | null)

                    if (element === null) {
                        continue
                    }

                    for (const placeholderData of placeholders) {
                        if (placeholderData.type === "attribute") {
                            const attributeName: string = placeholderData.attributeName || ""
                            // In an attribute type placeholder the attributeName should
                            //  always be defined, but we check it just in case
                            if (!attributeName) {
                                continue
                            }

                            const attributeValue: string = element.getAttribute(attributeName) || ""
                            element.setAttribute(
                                attributeName,
                                attributeValue.replace(
                                    placeholderData.placeholderKey,
                                    currentDataItem[placeholderData.dataItemKey],
                                ),
                            )
                        } else if (placeholderData.type === "text") {
                            for (const node of element.childNodes) {
                                if (node.nodeType === Node.TEXT_NODE) {
                                    node.textContent =
                                        node.textContent?.replace(
                                            placeholderData.placeholderKey,
                                            currentDataItem[placeholderData.dataItemKey],
                                        ) || ""
                                }
                            }
                        }
                    }
                }

                if (currentDataItem.events) {
                    for (const eventName in currentDataItem.events) {
                        itemElement.addEventListener(eventName, currentDataItem.events[eventName], { passive: true })
                    }
                }

                newListItems.push(itemElement)
                this.listContainerElement.appendChild(itemElement)
            }

            this.dispatchEvent(new CustomEvent(ListUpdatedEvent, { detail: { items: newListItems }, bubbles: true }))
        })
    }
}

declare global {
    interface HTMLElementEventMap {
        [ListUpdatedEvent]: CustomEvent<ListUpdatedEventDetail>
    }

    interface HTMLElementTagNameMap {
        [DynamicListTagName]: HTMLDynamicListElement<any>
    }
}

customElements.define(DynamicListTagName, HTMLDynamicListElement)
